import { useCallback, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { TimesheetProvider } from './contexts/TimesheetContext';
import { PersonaProvider } from './contexts/PersonaContext';
import { DataSourceProvider } from './contexts/DataSourceContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { EnteringForProvider } from './contexts/EnteringForContext';
import './utils/clearCache'; // Import to make clearTimesheetCache available globally
import { TimesheetGrid } from './components/TimesheetGrid';
import { ApprovalDashboard } from './components/ApprovalDashboard';
import { SidebarProvider } from './components/blocks/evo-sidebar';
import { LeftMenu } from './components/LeftMenu';
import { Header } from './components/Navigation/Header';
import { PersonaDashboard } from './components/dashboard/PersonaDashboard';
import { PlaceholderPage } from './components/Pages/PlaceholderPage';
import { AlertsDemoPage } from './components/Pages/AlertsDemoPage';
import { TimesheetReportsPage } from './components/Pages/TimesheetReportsPage';
import { MissingTimesheetsReportPage } from './components/Pages/MissingTimesheetsReportPage';
import { UtilisationReportPage } from './components/Pages/UtilisationReportPage';
import { AuditTrailPage } from './components/Pages/AuditTrailPage';
import { PostingLogPage } from './components/Pages/PostingLogPage';
import { SettingsPage } from './components/Pages/SettingsPage';
import { NotificationsPage } from './components/Pages/NotificationsPage';
import { ScreenSettingsPage } from './components/Pages/ScreenSettingsPage';
import { RolesAndRatesPage } from './components/Pages/RolesAndRatesPage';
import { RolesPermissionsPage } from './components/Pages/RolesPermissionsPage';
import { EditRolePage } from './components/Pages/EditRolePage';
import { ActiveProjectsPage } from './components/Pages/ActiveProjectsPage';
import { TeamMembersPage } from './components/Pages/TeamMembersPage';


import { FontWeightTest } from './components/FontWeightTest';
import { PageViewTracker } from './components/PageViewTracker';

import IntegrationsPage from './components/Pages/IntegrationsPage';
import IntegrationsProposalPage from './components/Pages/IntegrationsProposalPage';
import { CompanyProvider } from '@/features/multi-company';
import { useTimesheetDeepLink } from '@/hooks/useTimesheetDeepLink';

// Delegation feature routes
import { MyDelegationsPage } from '@/features/delegation/pages/MyDelegationsPage';
import { CreateDelegationPage } from '@/features/delegation/pages/CreateDelegationPage';
import { DelegationSettingsPage } from '@/features/delegation/pages/DelegationSettingsPage';
import { DelegationAuditPage } from '@/features/delegation/pages/DelegationAuditPage';

import { ApprovalTestPage } from './components/Pages/ApprovalTestPage';

import { WelcomeModal } from '@/components/ProductTour';
import { BootstrapGate } from '@/components/BootstrapGate';
import { OnboardingProvider } from '@/contexts/OnboardingContext';
import { UnsavedChangesProvider } from '@/contexts/UnsavedChangesContext';
import { OnboardingGate } from '@/components/OnboardingGate';
import {
  BarChart3,
  Users,
  Briefcase,
  Zap,
  Download,
  Settings,
  TrendingUp,
} from 'lucide-react';
import type { TimesheetStatus } from './types/timesheet';

// Navigation state types
type TimesheetDrilldownState = {
  employeeId: number;
  weekEnding: Date;
};

type ReportFiltersState = {
  statuses?: TimesheetStatus[];
  employeeIds?: number[];
  dateFrom?: Date;
  dateTo?: Date;
  quickDateRange?: string;
};

type AppNavigationState = {
  timesheetDrilldown?: TimesheetDrilldownState;
  reportFilters?: ReportFiltersState;
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [navigationState, setNavigationState] = useState<AppNavigationState | null>(null);

  const currentPath = location.pathname;

  const handleNavigate = (path: string, state?: AppNavigationState) => {
    setNavigationState(state ?? null);
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openTimesheetDeepLink = useCallback((timesheetDrilldown: TimesheetDrilldownState) => {
    setNavigationState({ timesheetDrilldown });
    navigate('/timesheet', { replace: true });
  }, [navigate]);
  useTimesheetDeepLink(openTimesheetDeepLink);

  return (
    <SidebarProvider>
      <UnsavedChangesProvider>
      <div className="flex h-screen w-full overflow-hidden">
        <LeftMenu activePage={currentPath} onPageChange={handleNavigate} />
        <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
          <Header currentPath={currentPath} onNavigate={handleNavigate} />
          
          {/* Main content with top padding to account for sticky header */}
          <main className="flex-1 min-h-0 overflow-auto pt-3 pb-3 bg-neutral-50">
            <div className="w-full max-w-[2000px] mx-auto h-full min-h-0 px-3">
            <Routes>
              {/* Homepage */}
              <Route path="/" element={<PersonaDashboard onNavigate={handleNavigate} />} />
              
              {/* Timesheet */}
              <Route path="/timesheet" element={<TimesheetGrid selection={navigationState?.timesheetDrilldown} />} />
              
              {/* Approvals */}
              <Route path="/approvals" element={<ApprovalDashboard />} />
              
              {/* Reports */}
              <Route path="/reports/timesheet" element={<TimesheetReportsPage onNavigate={handleNavigate} initialFilters={navigationState?.reportFilters} />} />
              <Route path="/reports/missing-timesheets" element={<MissingTimesheetsReportPage />} />
              <Route path="/reports/utilisation" element={<UtilisationReportPage initialFilters={navigationState?.reportFilters} />} />
              <Route path="/reports/audit-trail" element={<AuditTrailPage />} />
              <Route path="/reports/posting-log" element={<PostingLogPage />} />

              
              {/* Analytics route removed — belongs in the PSA suite's cross-module Analytics/BI module */}
              
              {/* Team */}
              <Route path="/team/members" element={<TeamMembersPage />} />

              
              {/* Projects */}
              <Route path="/projects/active" element={<ActiveProjectsPage />} />
              <Route
                path="/projects/archive"
                element={
                  <PlaceholderPage
                    title="Archived Projects"
                    description="View completed and archived projects"
                    icon={Briefcase}
                  />
                }
              />
              <Route
                path="/projects/budget"
                element={
                  <PlaceholderPage
                    title="Budget Tracking"
                    description="Project budget and forecasting"
                    icon={Briefcase}
                  />
                }
              />
              
              {/* Companies */}


              {/* Companies & Integrations */}
              <Route path="/integrations" element={<IntegrationsPage />} />
              <Route path="/integrations-proposal" element={<IntegrationsProposalPage />} />
              {/* Legacy integration routes - redirect to main companies page */}
              <Route path="/integrations/erp" element={<Navigate to="/integrations" replace />} />
              <Route path="/integrations/hr" element={<Navigate to="/integrations" replace />} />
              <Route path="/integrations/sync" element={<Navigate to="/integrations" replace />} />
              
              {/* Data */}
              <Route
                path="/data/import"
                element={
                  <PlaceholderPage
                    title="Import Data"
                    description="Import timesheet data from external sources"
                    icon={Download}
                  />
                }
              />
              <Route
                path="/data/export"
                element={
                  <PlaceholderPage
                    title="Export Data"
                    description="Export data to CSV, Excel, or PDF"
                    icon={Download}
                  />
                }
              />
              
              {/* Settings */}
              <Route path="/settings/general" element={<SettingsPage onNavigate={handleNavigate} />} />
              <Route path="/settings/organisation" element={<Navigate to="/settings/general" replace />} />
              <Route path="/settings/roles-permissions" element={<RolesPermissionsPage onNavigate={handleNavigate} />} />
              <Route path="/settings/roles-permissions/create" element={<EditRolePage onNavigate={handleNavigate} />} />
              <Route path="/settings/roles-permissions/edit/:roleId" element={<EditRolePage onNavigate={handleNavigate} />} />
              <Route path="/settings/screens" element={<ScreenSettingsPage />} />
              <Route path="/settings/roles" element={<RolesAndRatesPage onNavigate={handleNavigate} />} />
              <Route path="/settings/approvals" element={<ApprovalTestPage />} />
              <Route
                path="/settings/notifications"
                element={<NotificationsPage onNavigate={handleNavigate} />}
              />

              {/* Delegation — manager self-service */}
              <Route path="/approvals/delegations" element={<MyDelegationsPage />} />
              <Route path="/approvals/delegations/new" element={<CreateDelegationPage />} />

              {/* Delegation — admin settings (canonical path under Settings → Approvals) */}
              <Route path="/settings/approvals/delegation" element={<DelegationSettingsPage />} />
              {/* Delegation — admin audit report */}
              <Route path="/settings/approvals/delegation/audit" element={<DelegationAuditPage />} />
              {/* Legacy path kept as redirect for any existing links */}
              <Route path="/admin/settings/delegation" element={<Navigate to="/settings/approvals/delegation" replace />} />
              
              {/* Help — legacy routes redirect to homepage; help is now in the Help drawer */}
              <Route path="/help/docs" element={<Navigate to="/" replace />} />
              <Route path="/help/support" element={<Navigate to="/" replace />} />
              
              {/* Font Test Page */}
              <Route path="/font-test" element={<FontWeightTest />} />
              
              {/* Demo Pages */}
              <Route path="/demo/alerts" element={<AlertsDemoPage />} />
              
              {/* Fallback route */}
              <Route path="*" element={<PersonaDashboard onNavigate={handleNavigate} />} />
            </Routes>
            </div>
          </main>
        </div>
      </div>

      {/* Welcome Modal for first-time users */}
      <WelcomeModal tourId="welcome-tour" autoShow={true} />

      {/* Onboarding Wizard — renders via portal, visible to admins only */}
      <OnboardingGate />

      </UnsavedChangesProvider>
    </SidebarProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <PageViewTracker />
      <FeatureFlagProvider>
      <DataSourceProvider>
        <SettingsProvider>
          <CompanyProvider>
            <TimesheetProvider>
              <PermissionProvider>
                <EnteringForProvider>
                  <PersonaProvider isManager={true}>
                    <BootstrapGate>
                      <OnboardingProvider>
                        <AppContent />
                      </OnboardingProvider>
                    </BootstrapGate>
                  </PersonaProvider>
                </EnteringForProvider>
              </PermissionProvider>
            </TimesheetProvider>
          </CompanyProvider>
        </SettingsProvider>
      </DataSourceProvider>
      </FeatureFlagProvider>
    </BrowserRouter>
  );
}
